import {
    Body,
    breakpoints,
    Button,
    Collapsible,
    CollapsibleMethods,
    Spacer,
} from '@spritz-finance/ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import styled, { css } from 'styled-components'

import { FadePresence } from '../../../../../components/FadePresence'
import { CloseIcon } from '../../../../../components/icons/CloseIcon'
import { Row } from '../../../../../components/Layout/Row'
import { TestPaymentGradientText } from '../../../../../components/TestPaymentGradientText'
import { Feature, useFeatureFlag } from '../../../../../context/useFeatureFlag'
import { useTransactionPriceQuery } from '../../../../../graphql/generated/graphql'
import { formatCurrency } from '../../../../../utils/format'
import {
    TestPaymentLearnMoreWrapper,
    useTestPaymentInfoDialog,
} from '../../../useTestPaymentInfoDialog'
import { DirectPayType, useDirectPayContext } from '../context/DirectPayContext'
import { CreateDirectPayFormData } from '../usePayBill'
import { directPaymentValidation } from './amountInput/directPaymentValidation'
import { PaymentInputWrapper } from './amountInput/PaymentInputWrapper'
import { PriceBreakdown } from './amountInput/PriceBreakdown'
import { TogglePaymentDetails } from './amountInput/TogglePaymentDetails'
import { FormItemHeader } from './CreateDirectPaymentForm'
import { PaymentAmountInput } from './PaymentAmountInput'

const RewardsWrapper = styled.div``

const RewardsApplyWrapper = styled.div`
    height: 64px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background-color: var(--surface-3, #f2ffdc);
    padding: 8px 16px;
    margin-bottom: 16px;
`

const RemoveRewardsWrapper = styled.div`
    height: 36px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    background-color: var(--surface-3, #f2ffdc);
    padding: 8px 16px;
    margin-bottom: 16px;
`

const TestPaymentWrapper = styled.div`
    background: var(--surface-3, #f2ffdc);
    padding: 16px;
    border-radius: 10px;
`

const TestAmounts = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin-top: 16px;
`

const HighlightedAmount = css`
    cursor: pointer;
    border-color: transparent;
    box-shadow: 10px 10px 40px 0px var(--card-hover-shadow, rgba(0, 0, 0, 0.15));
`

const TestAmount = styled.div<{ active?: boolean }>`
    border-radius: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 700;
    font-size: 16px;
    width: 111px;
    height: 50px;
    border: 1px solid var(--text-primary, rgba(89, 89, 89, 1));
    background: var(
        --surface-2,
        linear-gradient(
            123.85deg,
            rgba(255, 255, 255, 0.85) 16.92%,
            rgba(255, 255, 255, 0.35) 85.33%
        )
    );
    transition: all 0.3s ease-in-out;
    &:hover {
        ${HighlightedAmount}
    }
    ${({ active }) =>
        active &&
        css`
            border: 1px solid rgba(0, 170, 207, 1);
        `}

    ${breakpoints.xsmall} {
        width: 70px;
    }
`
const formatAmountValue = (value: number | string) => {
    if (typeof value === 'string' && /(\.|,)$/g.test(value)) {
        return value.replace('.', '').replace(',', '')
    } else if (typeof value === 'string') {
        return value.replace(',', '.')
    }
    return value
}

const useAmountChange = (onChange: (amount: number) => void) => {
    const { watch } = useFormContext<CreateDirectPayFormData>()
    const currentAmount = useRef<number | undefined>(undefined)
    const amount = watch('amount')

    useEffect(() => {
        if (currentAmount.current !== amount) {
            currentAmount.current = amount
            onChange(Number(formatAmountValue(amount)))
        }
    }, [amount, onChange])
}

export const PaymentAmount = ({ disabled }: { disabled?: boolean }) => {
    const {
        rewardsBalance,
        type,
        testPaymentAvailable,
        selectedToken,
        rewardsPaymentAvailable,
        limits,
    } = useDirectPayContext()

    const {
        setValue,
        watch,
        setFocus,
        formState: { errors },
    } = useFormContext<CreateDirectPayFormData>()
    const priceBreakdown = useRef<CollapsibleMethods>(null)
    const [useRewards, setUseRewards] = useState(false)
    const [useTestPayment, setUseTestPayment] = useState(false)
    const [showPaymentBreakdown, setShowPaymentBreakdown] = useState(false)
    const pricingEnabled = useFeatureFlag(Feature.Pricing)

    const { openDialog } = useTestPaymentInfoDialog()

    const amount = watch('amount')
    const rewardsAmount = watch('rewardsAmount')

    const { data: transactionPriceQuery } = useTransactionPriceQuery({
        skip: !pricingEnabled || !amount,
        variables: {
            amount: Number(amount),
        },
    })

    const testTotals = useMemo(() => {
        const amt = Number(formatAmountValue(amount) ?? 0)
        const testAmt = Math.min(amt, 10)

        return {
            amount: amt,
            testAmount: testAmt,
            total: amt + testAmt,
        }
    }, [amount])

    const togglePaymentBreakdown = (show?: boolean) => {
        if (show && showPaymentBreakdown) return
        setShowPaymentBreakdown((curr) => !curr)
        priceBreakdown.current?.toggle()
    }

    const applyRewards = () => {
        setUseRewards(true)
        setValue('rewardsAmount', rewardsBalance)
        togglePaymentBreakdown(true)
    }

    const removeRewards = () => {
        setUseRewards(false)
        setValue('rewardsAmount', 0)
    }

    const [testAmount, setTestAmount] = useState<string | null>(null)

    const setTestPayment = (testAmt: any, otherAmt?: number) => {
        if (!testPaymentAvailable) {
            return
        }

        const amtValue = otherAmt ? formatAmountValue(otherAmt) : null

        setUseTestPayment(true)
        setTestAmount(testAmt)
        setValue('testPayment', true)
        togglePaymentBreakdown(true)
        if (testAmt === 'Other') {
            if (!amtValue) {
                // user clicked the 'Other' button
                setValue('amount', 0.0)
                setFocus('amount', { shouldSelect: true })
            }
        } else {
            setValue('amount', Number(testAmt.replace(/\D/, '')))
        }
    }

    useAmountChange((amount) => setTestPayment('Other', amount))

    const billPayTotal = useTestPayment
        ? Number(testTotals.total ?? 0)
        : Number(amount ?? 0) + Number(rewardsAmount ?? 0)

    const feeAmount = transactionPriceQuery?.transactionPrice ?? 0

    return (
        <>
            {testPaymentAvailable && (
                <div>
                    <TestPaymentWrapper>
                        <Row>
                            <Body.Med>
                                Make your first payment and we'll match
                                <TestPaymentGradientText>up to $10</TestPaymentGradientText>
                                <TestPaymentLearnMoreWrapper onClick={openDialog}>
                                    Learn more
                                </TestPaymentLearnMoreWrapper>
                            </Body.Med>
                        </Row>
                        <TestAmounts>
                            {['$1', '$5', '$10', 'Other'].map((testAmountOpt) => (
                                <TestAmount
                                    key={testAmountOpt}
                                    active={testAmountOpt === testAmount}
                                    onClick={() => setTestPayment(testAmountOpt)}
                                >
                                    {testAmountOpt}
                                </TestAmount>
                            ))}
                        </TestAmounts>
                    </TestPaymentWrapper>
                    <Spacer height={24} />
                </div>
            )}
            {rewardsPaymentAvailable && (
                <RewardsWrapper>
                    <FadePresence renderKey={useRewards}>
                        {!useRewards && (
                            <RewardsApplyWrapper onClick={applyRewards}>
                                <Row>
                                    <Body.SmBold>{formatCurrency(rewardsBalance)}</Body.SmBold>
                                    <Spacer width={4} />
                                    <Body.Med>reward balance</Body.Med>
                                </Row>
                                <Button.CTA style={{ width: '150px' }}>Apply</Button.CTA>
                            </RewardsApplyWrapper>
                        )}
                        {useRewards && (
                            <>
                                <RemoveRewardsWrapper onClick={removeRewards}>
                                    <Body.SmBold>
                                        <b>{formatCurrency(rewardsBalance)}</b> rewards applied
                                    </Body.SmBold>

                                    <CloseIcon />
                                </RemoveRewardsWrapper>
                            </>
                        )}
                    </FadePresence>
                </RewardsWrapper>
            )}
            <FormItemHeader>
                <Body.Med secondary size={12}>
                    {type === DirectPayType.SpritzCard ? 'Funding amount' : 'Payment amount'}
                </Body.Med>
                <Spacer height={4} />
            </FormItemHeader>
            <PaymentInputWrapper error={errors.amount?.message}>
                <PaymentAmountInput
                    type={testPaymentAvailable ? 'text' : 'number'}
                    disabled={disabled}
                    validation={directPaymentValidation(type, selectedToken, limits)}
                />
                <TogglePaymentDetails
                    expanded={showPaymentBreakdown}
                    onClick={() => togglePaymentBreakdown()}
                />
                <Collapsible ref={priceBreakdown} defaultOpen={false}>
                    <PriceBreakdown
                        key={rewardsAmount}
                        feeAmount={feeAmount}
                        billPayTotal={billPayTotal}
                        amount={amount}
                        testAmountActive={!!testAmount}
                        testAmount={testTotals.testAmount}
                        rewardsAmount={rewardsAmount}
                    />
                </Collapsible>
            </PaymentInputWrapper>
        </>
    )
}
